<template>
  <component
    :is="cardTag"
    v-bind="cardAttrs"
    class="trip-card"
    :class="[`trip-card-${variant}`, { 'trip-card-disabled': disabled }]"
    :aria-label="ariaLabel"
    :aria-disabled="disabled ? 'true' : null"
  >
    <v-img
      v-if="showHeroImage"
      :src="heroImage | mediaSrc"
      :alt="`${title} preview image`"
      :aspect-ratio="heroAspectRatio || 16 / 9"
      :contain="containImage || disabled"
      class="trip-card-image"
      gradient="to bottom, rgba(0,0,0,0.02), rgba(0,0,0,0.62)"
    >
      <div v-if="!containImage" class="trip-card-image-badge">
        <span v-if="badgeIcon" class="trip-card-badge-icon">{{ badgeIcon }}</span>
        <span>{{ tripType }}</span>
      </div>
    </v-img>

    <div class="trip-card-body">
      <div v-if="variant === 'compact'" class="trip-card-kicker">
        <span v-if="badgeIcon" class="trip-card-badge-icon">{{ badgeIcon }}</span>
        <span>{{ tripType }}</span>
      </div>

      <div v-else class="trip-card-kicker">{{ destination }} - {{ date }}</div>

      <h3 class="trip-card-title">{{ title }}</h3>
      <p v-if="subtitle" class="trip-card-subtitle">{{ subtitle }}</p>
      <p v-if="variant !== 'compact'" class="trip-card-summary">{{ summary }}</p>

      <div
        v-if="tags.length"
        class="trip-card-tags"
        aria-label="Trip tags"
      >
        <TagPill
          v-for="tag in visibleTags"
          :key="tag.slug"
          :label="tag.label"
          :category="tag.category"
          :to="tagRoute(tag)"
        />
      </div>

      <div v-if="showContextChips" class="trip-card-chips" aria-label="Trip details">
        <span v-if="isFamilyFriendly" class="trip-card-chip">Family-inclusive</span>
        <span v-if="crewCountLabel" class="trip-card-chip">{{ crewCountLabel }}</span>
      </div>

      <p
        v-if="isFamilyFriendly && familyNote"
        class="trip-card-family-note"
      >
        {{ familyNote }}
      </p>

      <div
        v-if="variant === 'compact' && earnedBadges.length"
        class="trip-card-compact-badges"
        aria-label="Earned badges"
      >
        <div
          v-for="badge in earnedBadges"
          :key="badge"
          class="trip-card-compact-badge"
        >
          <v-icon small>mdi-medal-outline</v-icon>
          <span>{{ badge }}</span>
        </div>
      </div>

      <div
        v-if="variant !== 'compact' && primaryHighlights.length"
        class="trip-card-highlights"
        aria-label="Trip highlights"
      >
        <div
          v-for="highlight in primaryHighlights"
          :key="highlight.text"
          class="trip-card-highlight"
        >
          <v-icon small>{{ highlight.icon }}</v-icon>
          <span>{{ highlight.text }}</span>
        </div>
      </div>

      <div v-if="variant !== 'compact' && featuredActivities.length" class="trip-card-list">
        <div class="trip-card-list-title">Highlights</div>
        <ul>
          <li
            v-for="activity in featuredActivities"
            :key="activity"
          >
            {{ activity }}
          </li>
        </ul>
      </div>

      <div v-if="showCta" class="trip-card-cta">
        <span>{{ ctaText || defaultCtaText }}</span>
        <v-icon small>mdi-arrow-right</v-icon>
      </div>
    </div>
  </component>
</template>

<script>
import TagPill from './TagPill.vue';

export default {
  name: 'TripCard',
  components: {
    TagPill,
  },
  props: {
    tripSlug: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    destination: {
      type: String,
      required: true,
    },
    date: {
      type: String,
      required: true,
    },
    tripType: {
      type: String,
      required: true,
      validator: (value) => [
        'Corporate Retreat',
        'Conference',
        'Adventure',
        'Holiday Party',
        'Annual Event',
      ].includes(value),
    },
    heroImage: {
      type: [String, Object],
      default: '',
    },
    heroAspectRatio: {
      type: Number,
      default: null,
    },
    containImage: {
      type: Boolean,
      default: false,
    },
    badgeIcon: {
      type: String,
      default: '',
    },
    summary: {
      type: String,
      required: true,
    },
    featuredActivities: {
      type: Array,
      default: () => [],
    },
    featuredMeals: {
      type: Array,
      default: () => [],
    },
    earnedBadges: {
      type: Array,
      default: () => [],
    },
    tags: {
      type: Array,
      default: () => [],
    },
    crewCount: {
      type: Number,
      default: null,
    },
    familyIncluded: {
      type: Boolean,
      default: false,
    },
    familyFriendly: {
      type: Boolean,
      default: false,
    },
    familyNote: {
      type: String,
      default: '',
    },
    ctaText: {
      type: String,
      default: '',
    },
    ctaUrl: {
      type: String,
      default: '',
    },
    to: {
      type: [String, Object],
      default: null,
    },
    variant: {
      type: String,
      default: 'featured',
      validator: (value) => ['compact', 'featured'].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showCta: {
      type: Boolean,
      default: true,
    },
    linkEnabled: {
      type: Boolean,
      default: true,
    },
    showImage: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    showHeroImage() {
      return this.showImage && this.variant !== 'compact' && Boolean(this.heroImage);
    },
    cardTag() {
      if (this.disabled || !this.linkEnabled) {
        return 'div';
      }

      if (this.to) {
        return 'router-link';
      }

      return this.ctaUrl ? 'a' : 'div';
    },
    cardAttrs() {
      if (this.disabled || !this.linkEnabled) {
        return {};
      }

      if (this.to) {
        return { to: this.to };
      }

      return this.ctaUrl ? { href: this.ctaUrl } : {};
    },
    defaultCtaText() {
      return this.variant === 'compact' ? 'View Trip' : 'View Adventure';
    },
    ariaLabel() {
      return `${this.ctaText || this.defaultCtaText}: ${this.title}`;
    },
    crewCountLabel() {
      if (!this.crewCount) {
        return '';
      }

      return `${this.crewCount} crew member${this.crewCount === 1 ? '' : 's'}`;
    },
    showContextChips() {
      return this.isFamilyFriendly || Boolean(this.crewCountLabel);
    },
    isFamilyFriendly() {
      return this.familyFriendly || this.familyIncluded;
    },
    visibleTags() {
      return this.variant === 'compact' ? this.tags.slice(0, 4) : this.tags.slice(0, 8);
    },
    primaryHighlights() {
      const highlights = [];
      const glacierActivity = this.featuredActivities.find((activity) => (
        activity.toLowerCase().includes('glacier')
      )) || this.featuredActivities[0];
      const lagoonActivity = this.featuredActivities.find((activity) => (
        activity.toLowerCase().includes('blue lagoon')
      )) || this.featuredActivities.find((activity) => (
        activity.toLowerCase().includes('lagoon')
      ));
      const featuredMeal = this.featuredMeals.find((meal) => (
        meal.toLowerCase().includes('fish')
      )) || this.featuredMeals[0];

      if (glacierActivity) {
        highlights.push({ icon: 'mdi-image-filter-hdr', text: glacierActivity });
      }

      if (lagoonActivity && lagoonActivity !== glacierActivity) {
        highlights.push({ icon: 'mdi-waves', text: lagoonActivity });
      }

      if (featuredMeal) {
        highlights.push({ icon: 'mdi-silverware-fork-knife', text: featuredMeal });
      }

      if (this.earnedBadges.length) {
        highlights.push({
          icon: 'mdi-medal-outline',
          text: `${this.earnedBadges.length} Merit Badge${this.earnedBadges.length === 1 ? '' : 's'}`,
        });
      }

      return highlights;
    },
  },
  methods: {
    tagRoute(tag) {
      return this.cardTag === 'div'
        ? { name: 'reckon-way-experience', params: { tagSlug: tag.slug } }
        : null;
    },
  },
};
</script>

<style scoped>
.trip-card {
  background: #101010;
  border: 1px solid #2b2b2b;
  border-radius: 8px;
  color: #fff;
  display: block;
  height: 100%;
  overflow: hidden;
  text-decoration: none;
  transition: border-color 180ms ease, transform 180ms ease;
}

.trip-card:not(.trip-card-disabled):hover,
.trip-card:not(.trip-card-disabled):focus {
  border-color: #e23232;
  color: #fff;
  transform: translateY(-4px);
}

.trip-card-disabled {
  cursor: default;
}

.trip-card-image {
  background-color: #050505;
}

.trip-card-image-badge {
  align-items: center;
  background: rgba(5, 5, 5, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-family: "Oswald", serif;
  font-size: 0.9rem;
  font-weight: 800;
  gap: 8px;
  left: 16px;
  letter-spacing: 0;
  padding: 7px 12px;
  position: absolute;
  text-transform: uppercase;
  top: 16px;
}

.trip-card-badge-icon {
  flex: 0 0 auto;
  line-height: 1;
}

.trip-card-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 22px;
}

.trip-card-compact .trip-card-body {
  padding: 18px;
}

.trip-card-kicker {
  color: #f0b13c;
  font-family: "Oswald", serif;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.25;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.trip-card-compact .trip-card-kicker {
  align-items: center;
  display: flex;
  gap: 8px;
}

.trip-card-title {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.75rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.08;
  margin: 0;
  overflow-wrap: anywhere;
}

.trip-card-compact .trip-card-title {
  font-size: 1.35rem;
}

.trip-card-subtitle {
  color: #e6e6e6;
  font-family: "Oswald", serif;
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.35;
  margin: 8px 0 0;
}

.trip-card-summary {
  color: #c9c9c9;
  font-size: 1rem;
  line-height: 1.55;
  margin: 14px 0 0;
}

.trip-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.trip-card-family-note {
  color: #d8d8d8;
  font-size: 0.95rem;
  font-style: italic;
  line-height: 1.45;
  margin: 12px 0 0;
}

.trip-card-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.trip-card-chip {
  background: rgba(226, 50, 50, 0.12);
  border: 1px solid rgba(226, 50, 50, 0.46);
  border-radius: 999px;
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0;
  padding: 5px 10px;
  text-transform: uppercase;
}

.trip-card-highlights {
  display: grid;
  gap: 9px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 18px;
}

.trip-card-highlight,
.trip-card-compact-badge {
  align-items: flex-start;
  color: #f5f5f5;
  display: flex;
  font-size: 0.95rem;
  gap: 8px;
  line-height: 1.35;
}

.trip-card-highlight .v-icon,
.trip-card-compact-badge .v-icon {
  color: #f0b13c;
  flex: 0 0 auto;
  margin-top: 1px;
}

.trip-card-list {
  border-top: 1px solid #252525;
  margin-top: 18px;
  padding-top: 16px;
}

.trip-card-list-title {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1rem;
  font-weight: 900;
  letter-spacing: 0;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.trip-card-list ul {
  display: grid;
  gap: 6px 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  list-style: none;
  margin: 0;
  padding: 0;
}

.trip-card-list li {
  color: #cfcfcf;
  font-size: 0.95rem;
  line-height: 1.35;
  padding-left: 18px;
  position: relative;
}

.trip-card-list li::before {
  color: #e23232;
  content: "\2713";
  font-weight: 900;
  left: 0;
  position: absolute;
  top: 0;
}

.trip-card-compact-badges {
  display: grid;
  gap: 9px;
  margin-top: 16px;
}

.trip-card-cta {
  align-items: center;
  color: #fff;
  display: inline-flex;
  font-family: "Oswald", serif;
  font-size: 1rem;
  font-weight: 900;
  gap: 8px;
  letter-spacing: 0;
  margin-top: auto;
  padding-top: 20px;
  text-transform: uppercase;
}

.trip-card-cta .v-icon {
  color: #e23232;
}

@media (max-width: 700px) {
  .trip-card-highlights,
  .trip-card-list ul {
    grid-template-columns: 1fr;
  }

  .trip-card-title {
    font-size: 1.5rem;
  }
}
</style>
